"use client";

import { useEffect, useRef, useState } from "react";
import { Pencil } from "lucide-react";

/**
 * Variant controls typography and empty-state rendering.
 *
 * - `title`: 22px / 600, white gradient via `-webkit-background-clip: text`.
 * - `composer`: 13px / 400. Empty value is rendered as italic muted text.
 */
export type InlineEditableFieldVariant = "title" | "composer";

/**
 * Props for {@link InlineEditableField}.
 */
export interface InlineEditableFieldProps {
  /** Current value (controlled). */
  value: string;
  /** Placeholder shown when value is empty (and as the input placeholder). */
  placeholder?: string;
  /** Visual variant. See {@link InlineEditableFieldVariant}. */
  variant: InlineEditableFieldVariant;
  /**
   * Called on EVERY keystroke while editing (not only on commit/blur).
   *
   * IMPORTANT: this is required by the parent's anti-stomp contract — typically
   * a `userHasEditedTitleRef` that flips on first user input to prevent the
   * async analysis result from overwriting what the user has typed.
   */
  onChange: (next: string) => void;
  /**
   * Optional accessible label for the underlying button/input. Defaults to the
   * placeholder, then to a sensible variant-specific label.
   */
  ariaLabel?: string;
}

const TITLE_FONT_SIZE = 22;
const TITLE_FONT_WEIGHT = 600;
const TITLE_LETTER_SPACING = "-0.01em";

const COMPOSER_FONT_SIZE = 13;
const COMPOSER_FONT_WEIGHT = 400;

const TITLE_GRADIENT =
  "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.75) 100%)";

/**
 * Inline-editable single-line field used by the upload modal to let the user
 * fix the auto-detected title and composer. Click anywhere on the field — or
 * focus it via keyboard — to switch to edit mode.
 *
 * Behaviour:
 * - On EVERY keystroke, calls `onChange(value)` so the parent can track user
 *   intent and short-circuit incoming auto-fill data.
 * - `Enter` commits and blurs. `Escape` blurs without further change (parent
 *   keeps the last keystroke value — it cannot be "undone" since onChange has
 *   already fired live).
 * - A small pencil icon appears on hover or while editing.
 */
export function InlineEditableField({
  value,
  placeholder = "",
  variant,
  onChange,
  ariaLabel,
}: InlineEditableFieldProps) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isTitle = variant === "title";
  const empty = value.trim() === "";

  const fontSize = isTitle ? TITLE_FONT_SIZE : COMPOSER_FONT_SIZE;
  const fontWeight = isTitle ? TITLE_FONT_WEIGHT : COMPOSER_FONT_WEIGHT;
  const letterSpacing = isTitle ? TITLE_LETTER_SPACING : "0";

  const accessibleLabel =
    ariaLabel ??
    placeholder ??
    (isTitle ? "Titre du morceau" : "Compositeur du morceau");

  useEffect(() => {
    if (!editing) {
      return;
    }

    const node = inputRef.current;

    if (!node) {
      return;
    }

    node.focus();
    node.select();
  }, [editing]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === "Escape") {
      event.currentTarget.blur();
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={accessibleLabel}
        className="bg-transparent outline-none w-full"
        style={{
          fontSize,
          fontWeight,
          color: "rgba(255, 255, 255, 0.95)",
          letterSpacing,
          borderBottom: "1px solid var(--color-accent-bright)",
          borderBottomColor: "rgba(167, 139, 250, 0.5)",
          padding: "0 0 1px 0",
          lineHeight: 1.2,
        }}
      />
    );
  }

  // Display mode.
  const titleVisible = isTitle && !empty;
  const composerColor = empty
    ? "var(--color-text-muted)"
    : "var(--color-text-primary)";
  const composerOpacity = empty ? 1 : 0.92;
  const composerFontStyle = empty ? "italic" : "normal";

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      aria-label={accessibleLabel}
      className="group inline-flex items-baseline gap-2 max-w-full text-left"
      style={{ cursor: "text" }}
    >
      {titleVisible ? (
        <span
          className="truncate"
          style={{
            fontSize,
            fontWeight,
            background: TITLE_GRADIENT,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            color: "transparent",
            letterSpacing,
            lineHeight: 1.2,
          }}
        >
          {value}
        </span>
      ) : (
        <span
          className="truncate"
          style={{
            fontSize,
            fontWeight,
            color: isTitle ? "var(--color-text-muted)" : composerColor,
            opacity: isTitle ? 1 : composerOpacity,
            fontStyle: isTitle
              ? empty
                ? "italic"
                : "normal"
              : composerFontStyle,
            letterSpacing,
            lineHeight: 1.2,
          }}
        >
          {empty ? placeholder : value}
        </span>
      )}
      <Pencil
        size={11}
        strokeWidth={1.8}
        aria-hidden
        className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 shrink-0"
        style={{ color: "rgba(255, 255, 255, 0.40)" }}
      />
    </button>
  );
}
